import type { Dispatch, SetStateAction } from 'react';
import React from 'react';
import type { ViewProps } from 'react-native';
import { Pressable, StyleSheet } from 'react-native';
import { View } from 'react-native';
import type { IconName } from '@sg/garnish';
import { BodyText, Icon, TextLink, theme } from '@sg/garnish';

import { CheckoutLayoutBlock } from '@order/components';

import { useLocalizationContext } from '../../context';
import type {
  BraintreeCardType,
  SubscriptionCreditCard,
} from '../../graphql/types';
import { CheckoutPaymentBlockCardIcons } from '../CheckoutScreen/components/Blocks/CheckoutPaymentBlock/CheckoutPaymentBlock.cardIcons';

export const BRAINTREE_CREDIT_CARDS_ICONS: Record<BraintreeCardType, IconName> =
  {
    AmEx: 'IconCcAmex',
    CarteBlanche: 'IconCreditCard',
    ChinaUnionPay: 'IconCreditCard',
    DinersClubInternational: 'IconCreditCard',
    Discover: 'IconCcDiscover',
    Elo: 'IconCreditCard',
    JCB: 'IconCreditCard',
    Laser: 'IconCreditCard',
    Maestro: 'IconCreditCard',
    MasterCard: 'IconCcMasterCard',
    Solo: 'IconCreditCard',
    Switch: 'IconCreditCard',
    UKMaestro: 'IconCreditCard',
    Visa: 'IconCcVisa',
    Unknown: 'IconCreditCard',
  };

type SweetpassCheckoutCardBlockProps = {
  readonly creditCards?: readonly SubscriptionCreditCard[];
  readonly onReplaceCreditCardBtnPress: (
    creditCardId: SubscriptionCreditCard['id'],
  ) => void;
  readonly onAddCreditCardBtnPress: () => void;
  readonly savedCardHandler: Dispatch<SetStateAction<string>>;
} & Pick<ViewProps, 'testID'>;

type ReplaceCreditCardRowProps = {
  readonly creditCard: SubscriptionCreditCard;
} & Pick<
  SweetpassCheckoutCardBlockProps,
  'onReplaceCreditCardBtnPress' | 'testID'
>;

const ReplaceCreditCardRow = ({
  testID,
  creditCard,
  onReplaceCreditCardBtnPress,
}: ReplaceCreditCardRowProps) => {
  const { t } = useLocalizationContext();
  const creditCardIcon = BRAINTREE_CREDIT_CARDS_ICONS[creditCard.cardType];

  return (
    <View testID={testID} style={styles.creditCardWrapper}>
      <View style={styles.creditCardName}>
        <Icon style={styles.creditCardIcon} name={creditCardIcon} />
        <BodyText size={4}>
          {t('sweetpass.checkout.credit-card.description', {
            cardType: creditCard.cardType,
            lastFour: creditCard.lastFour,
          })}
        </BodyText>
      </View>
      <TextLink
        underline
        onPress={() => {
          onReplaceCreditCardBtnPress(creditCard.id);
        }}
      >
        {t('sweetpass.checkout.credit-card.replace')}
      </TextLink>
    </View>
  );
};

const AddCreditCardRow = ({
  testID,
  onAddCreditCardBtnPress,
}: Pick<
  SweetpassCheckoutCardBlockProps,
  'onAddCreditCardBtnPress' | 'testID'
>) => {
  const { t } = useLocalizationContext();

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      onPress={onAddCreditCardBtnPress}
    >
      <View style={styles.creditCardWrapper}>
        <View style={styles.creditCardName}>
          <Icon
            style={styles.creditCardIcon}
            name={BRAINTREE_CREDIT_CARDS_ICONS.Unknown}
          />
          <BodyText size={4}>
            {t('sweetpass.checkout.credit-card.new')}
          </BodyText>
        </View>
        <Icon name="IconCaretRight" />
      </View>
    </Pressable>
  );
};

export const SweetpassCheckoutCardBlock = ({
  testID,
  creditCards,
  onReplaceCreditCardBtnPress,
  onAddCreditCardBtnPress,
  savedCardHandler,
}: SweetpassCheckoutCardBlockProps) => {
  const creditCard = creditCards?.[0];

  savedCardHandler(creditCard?.id ?? '');

  return (
    <CheckoutLayoutBlock testID={testID}>
      <CheckoutPaymentBlockCardIcons />
      {creditCard ? (
        <ReplaceCreditCardRow
          testID={`${testID}.replace`}
          creditCard={creditCard}
          onReplaceCreditCardBtnPress={onReplaceCreditCardBtnPress}
        />
      ) : (
        <AddCreditCardRow
          testID={`${testID}.add`}
          onAddCreditCardBtnPress={onAddCreditCardBtnPress}
        />
      )}
    </CheckoutLayoutBlock>
  );
};

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  creditCardWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  creditCardName: {
    paddingVertical: theme.spacing.large,
    flexDirection: 'row',
    alignItems: 'center',
  },
  creditCardIcon: {
    marginRight: theme.spacing.medium,
  },
});
